/**
 * Accessibility patches for markup injected by app embeds we cannot edit.
 *
 * WAVE flags these on every page (see .cursor/references/theme-check-remediation-patterns.md):
 * - Timesact back-in-stock popup: unlabeled email input, empty heading, empty submit button,
 *   empty newsletter label, unlabeled "by Timesact" logo SVG, empty `md-trigger` modal button.
 * - Alhena AI chat: logo SVG inside an already-labeled link.
 * - Truemed (HSA/FSA) on PDPs: decorative loading-spinner SVG with no text alternative.
 *
 * Timesact fills the heading/button/label text only when the popup opens for a product, so
 * empty elements get a visually hidden fallback that is removed as soon as Timesact writes its
 * own text (keeps the accessible name equal to the visible text, WCAG 2.5.3).
 *
 * Loaded as a classic deferred script (not a module) so `document.currentScript` exposes the
 * translated strings passed by snippets/third-party-a11y.liquid.
 */
(() => {
  const script = document.currentScript;
  if (!(script instanceof HTMLScriptElement)) return;

  const strings = script.dataset;
  const FALLBACK_ATTR = 'data-third-party-a11y-fallback';
  const OBSERVE_MS = 30000;

  /** Elements whose text Timesact fills in later, mapped to their fallback text. */
  const fillers = [
    { selector: '.timesact_bis_heading', text: strings.backInStockHeading },
    { selector: '.timesact_bis_submit_button', text: strings.backInStockSubmit },
    { selector: 'label.timesact_bis_subscribe', text: strings.backInStockNewsletter },
  ];

  /**
   * Text of an element, ignoring our own fallback span.
   * @param {Element} element
   * @returns {string}
   */
  function ownText(element) {
    let text = '';
    for (const node of element.childNodes) {
      if (node instanceof Element && node.hasAttribute(FALLBACK_ATTR)) continue;
      text += node.textContent ?? '';
    }
    return text.trim();
  }

  /**
   * Adds the fallback span while the element is empty, and removes it once it has real text.
   * Idempotent, so it is safe to call from a MutationObserver.
   * @param {Element} element
   * @param {string | undefined} text
   */
  function syncFallback(element, text) {
    if (!text) return;
    const fallback = element.querySelector(`:scope > [${FALLBACK_ATTR}]`);

    if (ownText(element)) {
      fallback?.remove();
    } else if (!fallback) {
      const span = document.createElement('span');
      span.className = 'visually-hidden';
      span.setAttribute(FALLBACK_ATTR, '');
      span.textContent = text;
      element.append(span);
    }
  }

  /**
   * Hides a decorative SVG from assistive tech (its link already has a name).
   * @param {Element | null} svg
   */
  function hideDecorativeSvg(svg) {
    if (!svg || svg.getAttribute('aria-hidden') === 'true') return;
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
  }

  /** @type {MutationObserver | null} */
  let dialogObserver = null;

  /**
   * Keeps the Timesact fallbacks in sync for the lifetime of the page. Scoped to the popup, so
   * it only fires when Timesact rewrites its own markup.
   * @param {Element} dialog
   */
  function watchDialog(dialog) {
    if (dialogObserver) return;
    const sync = () => {
      for (const { selector, text } of fillers) {
        for (const element of dialog.querySelectorAll(selector)) syncFallback(element, text);
      }
    };
    sync();
    dialogObserver = new MutationObserver(sync);
    dialogObserver.observe(dialog, { childList: true, subtree: true, characterData: true });
  }

  function patch() {
    const dialog = document.querySelector('.timesact_bis_dialog');
    if (dialog) watchDialog(dialog);

    const email = document.querySelector('input#timesact_bis_email');
    if (email && !email.hasAttribute('aria-label') && strings.backInStockEmail) {
      email.setAttribute('aria-label', strings.backInStockEmail);
      email.setAttribute('autocomplete', 'email');
    }

    for (const svg of document.querySelectorAll('.timesact_powered_by a svg')) hideDecorativeSvg(svg);

    const trigger = document.querySelector('.popup-container button.md-trigger[data-modal="mixed-modal"]');
    if (trigger instanceof HTMLElement && !trigger.textContent?.trim() && !trigger.hasAttribute('aria-label')) {
      if (strings.preorderNoticeOpen) trigger.setAttribute('aria-label', strings.preorderNoticeOpen);
      // An invisible trigger should not be a Tab stop.
      if (!trigger.offsetWidth && !trigger.offsetHeight) trigger.tabIndex = -1;
    }

    for (const svg of document.querySelectorAll('a[href*="alhena.ai"][aria-label] svg')) hideDecorativeSvg(svg);

    for (const svg of document.querySelectorAll('#truemed-instructions-container svg.tm-spinner')) {
      hideDecorativeSvg(svg);
    }
  }

  // App embeds inject after load at unpredictable times; watch briefly, batching per frame.
  let scheduled = false;
  const bodyObserver = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      patch();
    });
  });

  patch();
  bodyObserver.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => bodyObserver.disconnect(), OBSERVE_MS);
})();
